import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { getUnidad } from "../../../lib/unidades";
import { LayoutDashboard, Package, MapPin, ChefHat, BookOpen, ArrowLeft, ShoppingCart, Factory, Truck, Layers, DollarSign } from "lucide-react";
import InventarioTab from "./InventarioTab";
import UbicacionesTab from "./UbicacionesTab";
import RecetasTab from "./RecetasTab";
import MenusTab from "./MenusTab";
import ResumenTab from "./ResumenTab";
import { ComprasTab, ProduccionTab, ProveedoresTab, CatalogoTab, CosteoTab } from "./ListadosOperativos";

// Tabs personalizadas por unidad (autorizadas 2026-07-23)
const TABS_POR_UNIDAD = {
  bocatto: [
    { id: "resumen",     label: "Resumen",     icon: LayoutDashboard },
    { id: "inventario",  label: "Inventario",  icon: Package },
    { id: "ubicaciones", label: "Ubicaciones", icon: MapPin },
    { id: "recetas",     label: "Recetas",     icon: BookOpen },
    { id: "menus",       label: "Menús",       icon: ChefHat },
    { id: "compras",     label: "Compras",     icon: ShoppingCart },
    { id: "produccion",  label: "Producción",  icon: Factory },
    { id: "proveedores", label: "Proveedores", icon: Truck },
    { id: "catalogo",    label: "Catálogo",    icon: Layers },
    { id: "costeo",      label: "Costeo",      icon: DollarSign },
  ],
  petit: [
    { id: "resumen",     label: "Resumen",     icon: LayoutDashboard },
    { id: "ubicaciones", label: "Espacios",    icon: MapPin },
    { id: "inventario",  label: "Mobiliario",  icon: Package },
    { id: "catalogo",    label: "Catálogo",    icon: Layers },
    { id: "proveedores", label: "Proveedores", icon: Truck },
    { id: "compras",     label: "Compras",     icon: ShoppingCart },
    { id: "costeo",      label: "Costeo",      icon: DollarSign },
  ],
  kermesita: [
    { id: "resumen",     label: "Resumen",     icon: LayoutDashboard },
    { id: "catalogo",    label: "Servicios",   icon: Layers },
    { id: "inventario",  label: "Equipo",      icon: Package },
    { id: "ubicaciones", label: "Ubicaciones", icon: MapPin },
    { id: "proveedores", label: "Proveedores", icon: Truck },
    { id: "compras",     label: "Compras",     icon: ShoppingCart },
    { id: "costeo",      label: "Costeo",      icon: DollarSign },
  ],
};

export default function PanelUnidad({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const unidad = getUnidad(slug);
  const [tab, setTab] = useState("resumen");

  if (!unidad) {
    return <Navigate to="/panel/dashboard-unidades" replace />;
  }
  const Icon = unidad.icon;
  const color = unidad.color;
  const colorSoft = unidad.color_soft;
  const TABS = TABS_POR_UNIDAD[slug] || TABS_POR_UNIDAD.bocatto;

  const renderTab = () => {
    switch (tab) {
      case "inventario":  return <InventarioTab unidadSlug={slug} color={color} />;
      case "ubicaciones": return <UbicacionesTab unidadSlug={slug} color={color} />;
      case "recetas":     return <RecetasTab unidadSlug={slug} color={color} />;
      case "menus":       return <MenusTab unidadSlug={slug} color={color} />;
      case "compras":     return <ComprasTab unidadSlug={slug} color={color} />;
      case "produccion":  return <ProduccionTab unidadSlug={slug} color={color} />;
      case "proveedores": return <ProveedoresTab unidadSlug={slug} color={color} />;
      case "catalogo":    return <CatalogoTab unidadSlug={slug} color={color} />;
      case "costeo":      return <CosteoTab unidadSlug={slug} color={color} colorSoft={colorSoft} />;
      default:            return <ResumenTab unidadSlug={slug} color={color} colorSoft={colorSoft} />;
    }
  };

  return (
    <div className="space-y-4" data-testid={`panel-unidad-${slug}`}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: colorSoft }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl truncate" style={{ color }}>{unidad.nombre}</h1>
            <p className="text-xs sm:text-sm text-stone-500 truncate">Panel operativo · {unidad.descripcion_corta}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/panel/dashboard-unidades"
            className="text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center gap-1"
            style={{ color }}
            data-testid="link-dashboard-general"
          >
            <ArrowLeft className="w-3 h-3" /> Dashboard General
          </Link>
        </div>
      </div>

      {/* Tabs — scrollable en mobile */}
      <div className="border-b border-stone-200 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => {
            const TabIcon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active ? "border-current" : "border-transparent text-stone-500 hover:text-stone-800"
                }`}
                style={active ? { color, borderBottomColor: color } : {}}
                data-testid={`tab-${t.id}`}
              >
                <TabIcon className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenido */}
      <div className="pt-2">{renderTab()}</div>
    </div>
  );
}

// Wrappers para cada unidad (usados en rutas de App.js)
export const PanelPetit = () => <PanelUnidad slug="petit" />;
export const PanelKermesita = () => <PanelUnidad slug="kermesita" />;
export const PanelBocatto = () => <PanelUnidad slug="bocatto" />;
